import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { faMinusCircle } from '@fortawesome/pro-regular-svg-icons';
import IconButton from '@components/IconButton';
import SearchableTable from '@components/SearchableTable';
import EditMemberModal from './EditMemberModal';
import { selectRules } from '@pathways/redux/rules/reducers';
import { fetchRules } from '@pathways/redux/rules/actions';
import {
  createCareTeamRoleMember,
  editCareTeamRoleMember,
  deleteCareTeamRoleMember,
} from '@redux/careTeam/actions';
import './style.less';

function CareTeamMembers({ appUserPathwayCareTeam, appUserPathway, appUserId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [rulesLoading, rulesFromSelector] = useSelector(selectRules);

  useEffect(() => {
    if (!rulesLoading && rulesFromSelector.length === 0) {
      dispatch(fetchRules());
    }
  }, [rulesLoading, rulesFromSelector, dispatch]);

  const currentRoles = useMemo(() => {
    return appUserPathwayCareTeam || [];
  }, [appUserPathwayCareTeam]);

  const currentMembers = useMemo(() => {
    if (!appUserPathwayCareTeam) return [];

    return appUserPathwayCareTeam.flatMap(role =>
      role.members.map(member => ({
        ...member,
        role: role.id,
      })),
    );
  }, [appUserPathwayCareTeam]);

  const dataSource = useMemo(() => {
    return currentRoles.map(role => {
      const member = currentMembers.find(m => m.role === role.id);
      return {
        key: role.id,
        roleId: role.id,
        roleName: role.name,
        roleDescription: role.description,
        rolePermissions: role.permissions,
        memberName: member?.name || '',
        memberEmail: member?.email || '',
        memberPhone: member?.phone || '',
        memberBusinessName: member?.businessName || '',
        memberId: member?.id || null,
        hasMember: !!member,
      };
    });
  }, [currentRoles, currentMembers]);

  const columns = [
    {
      title: t('patients:Pathways.careTeam.columns.role'),
      dataIndex: 'roleName',
      key: 'roleName',
      width: 150,
      render: text => <p className="care-team-members-role-name">{text}</p>,
    },
    {
      title: t('patients:Pathways.careTeam.columns.name'),
      dataIndex: 'memberName',
      key: 'memberName',
      width: 150,
    },
    {
      title: t('patients:Pathways.careTeam.columns.email'),
      dataIndex: 'memberEmail',
      key: 'memberEmail',
      width: 200,
    },
    {
      title: t('patients:Pathways.careTeam.columns.phone'),
      dataIndex: 'memberPhone',
      key: 'memberPhone',
      width: 150,
    },
    {
      title: t('patients:Pathways.careTeam.columns.businessName'),
      dataIndex: 'memberBusinessName',
      key: 'memberBusinessName',
      width: 200,
    },
  ];

  const filterFunc = useCallback((row, comparator) => {
    const searchableFields = [
      row.roleName,
      row.memberName,
      row.memberEmail,
      row.memberPhone,
      row.memberBusinessName,
    ];
    return searchableFields.some(field => comparator(field || ''));
  }, []);

  const handleRowClick = useCallback(
    row => {
      const role = currentRoles.find(r => r.id === row.roleId);
      const member = currentMembers.find(m => m.role === row.roleId);

      setEditingRole({
        ...role,
        member: member || null,
      });
      setModalVisible(true);
    },
    [currentRoles, currentMembers],
  );

  const handleSaveMember = useCallback(
    memberData => {
      const existingMember = editingRole?.member;
      const appUserPathwayId = appUserPathway?.id?.toString();

      if (existingMember?.id) {
        dispatch(
          editCareTeamRoleMember(
            appUserId,
            editingRole.id,
            existingMember.id,
            {
              name: memberData.name,
              email: memberData.email,
              phone: memberData.phone,
              businessName: memberData.businessName,
            },
            appUserPathwayId,
          ),
        );
      } else {
        dispatch(
          createCareTeamRoleMember(
            appUserId,
            editingRole.id,
            {
              name: memberData.name,
              email: memberData.email,
              phone: memberData.phone,
              businessName: memberData.businessName,
            },
            appUserPathwayId,
          ),
        );
      }

      setModalVisible(false);
      setEditingRole(null);
    },
    [dispatch, appUserId, appUserPathway, editingRole],
  );

  const handleRemoveMember = useCallback(() => {
    const appUserPathwayId = appUserPathway?.id?.toString();
    selectedRows.forEach(row => {
      if (row.hasMember && row.memberId) {
        dispatch(deleteCareTeamRoleMember(appUserId, row.roleId, row.memberId, appUserPathwayId));
      }
    });
    setSelectedRows([]);
  }, [dispatch, appUserId, appUserPathway, selectedRows]);

  const canRemove = useMemo(() => {
    return selectedRows.length > 0 && selectedRows.some(row => row.hasMember);
  }, [selectedRows]);

  if (!appUserPathwayCareTeam) return null;

  return (
    <>
      <SearchableTable
        className="care-team-members-table"
        columns={columns}
        dataSource={dataSource}
        extra={
          <div className="care-team-members-actions">
            <IconButton
              disabled={!canRemove}
              icon={faMinusCircle}
              tooltip={t('patients:Pathways.careTeam.buttons.remove')}
              onClick={handleRemoveMember}
            />
          </div>
        }
        filterFunc={filterFunc}
        loading={false}
        pagination={false}
        rowKey="key"
        title={t('patients:Pathways.careTeam.title')}
        onRowClick={handleRowClick}
        onRowSelectionChange={(_, rows) => setSelectedRows(rows)}
      />
      <EditMemberModal
        visible={modalVisible}
        role={editingRole}
        onSave={handleSaveMember}
        onCancel={() => {
          setModalVisible(false);
          setEditingRole(null);
        }}
        appUserPathway={appUserPathway}
      />
    </>
  );
}

export default CareTeamMembers;
